import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Input, message, Select, Skeleton, Space } from 'antd';
import {
	CheckOutlined,
	DeleteOutlined,
	LockOutlined,
	PhoneOutlined,
	PlusCircleOutlined,
	UndoOutlined,
	UnlockOutlined,
} from '@ant-design/icons';
import { Page, PageSection, WarningModal } from '../../components.js';
import { Formatting, getTelHref } from '../../utils';
import { ClientOrderPaymentState, ClientType, UserLevel } from 'types';
import { AsyncButton } from 'components';
import { EditClientDto } from 'API.js';
import { useClientDetails } from './use-client-details.js';
import { OrdersSection } from './orders-section.js';
import { useUserLevel } from 'reducers';

import '../../styles/pages/clients/clientDetails.scss';

const { Option } = Select;
const { TextArea } = Input;

export const ClientDetails = () => {
	const navigate = useNavigate();
	const params = useParams();
	const id = Number.parseInt(params['id']!);
	const userLevel = useUserLevel();

	const [deleteModal, setDeleteModal] = useState(false);

	const {
		client,
		orders,
		containers,
		createdBy,
		removedBy,
		lockedBy,
		addresses,
		loading,
		saveChanges,
		deleteClient,
		restoreClient,
		lockClient,
		unlockClient,
		payOrder,
	} = useClientDetails(id);
	const locked = client?.lockedDate !== null;

	const [name, setName] = useState<string>();
	const [phone, setPhone] = useState<string>();
	const [type, setType] = useState<ClientType>();
	const [email, setEmail] = useState<string>();
	const [nip, setNip] = useState<string>();
	const [bdo, setBdo] = useState<string>();
	const [street, setStreet] = useState<string>();
	const [city, setCity] = useState<string>();
	const [postal, setPostal] = useState<string>();
	const [description, setDescription] = useState<string>();

	if (!client || !orders || !createdBy || !addresses || !containers) {
		return <Skeleton active />;
	}

	const hasAnyChanges = () => {
		if (name !== undefined && name.trim() != client.name) {
			return true;
		}

		if (type !== undefined && type != client.type) {
			return true;
		}

		if (phone !== undefined && phone.trim() != client.phone) {
			return true;
		}

		if (email !== undefined && email.trim() != client.email) {
			return true;
		}

		if (nip !== undefined && nip.trim() != client.nip) {
			return true;
		}

		if (bdo !== undefined && bdo.trim() != client.bdo) {
			return true;
		}

		if (street !== undefined && street.trim() != client.street) {
			return true;
		}

		if (city !== undefined && city.trim() != client.city) {
			return true;
		}

		if (postal !== undefined && postal.trim() != client.postal) {
			return true;
		}

		if (description !== undefined && description.trim() != client.description) {
			return true;
		}

		return false;
	};

	const _saveChanges = async () => {
		const dto: EditClientDto = {};

		if (name !== undefined) {
			const n = name.trim();
			if (n === '') {
				return message.error('Podaj nazwę');
			}

			if (n.length > 64) {
				return message.error('Nazwa jest za długa');
			}

			if (n !== client.name) {
				dto.name = n;
			}
		}

		if (type !== undefined && type !== client.type) {
			dto.type = type;
		}

		if (phone !== undefined) {
			const p = phone.trim();
			if (p === '') {
				return message.error('Podaj numer telefon');
			}

			if (!p.match(/^(\+[0-9]{2} ?)?([0-9] ?){9}$/)) {
				return message.error('Numer telefonu jest niepoprawny');
			}

			if (p.length > 16) {
				return message.error('Numer telefonu jest za długi');
			}

			if (p !== client.phone) {
				dto.phone = p;
			}
		}

		if (email !== undefined) {
			const e = email.trim();
			if (e === '') {
				dto.email = null;
			} else if (!e.match(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)) {
				return message.error('Email jest niepoprawny');
			} else if (e.length > 64) {
				return message.error('Email jest za długi');
			} else if (e !== client.email) {
				dto.email = e;
			}
		}

		if (nip !== undefined) {
			const n = nip.trim();
			if (n === '') {
				dto.nip = null;
			} else if (!n.match(/^[0-9]{10}$/)) {
				return message.error('NIP jest niepoprawny');
			} else if (n.length > 10) {
				return message.error('NIP jest za długi');
			} else if (n !== client.nip) {
				dto.nip = n;
			}
		}

		if (bdo !== undefined) {
			const b = bdo.trim();
			if (b === '') {
				dto.bdo = null;
			} else if (!b.match(/^[0-9]{8}$/)) {
				return message.error('BDO jest niepoprawny');
			} else if (b.length > 14) {
				return message.error('BDO jest za długi');
			} else if (b !== client.bdo) {
				dto.bdo = b;
			}
		}

		if (street !== undefined) {
			const s = street.trim();
			if (s === '') {
				dto.street = null;
			} else if (s.length > 64) {
				return message.error('Ulica jest za długa');
			} else if (s !== client.street) {
				dto.street = s;
			}
		}

		if (city !== undefined) {
			const c = city.trim();
			if (c === '') {
				dto.city = null;
			} else if (c.length > 64) {
				return message.error('Miasto jest za długie');
			} else if (c !== client.city) {
				dto.city = c;
			}
		}

		if (postal !== undefined) {
			const p = postal.trim();
			if (p === '') {
				dto.postal = null;
			} else if (!p.match(/^[0-9]{2}-[0-9]{3}$/)) {
				return message.error('Kod pocztowy jest niepoprawny');
			} else if (p.length > 16) {
				return message.error('Kod pocztowy jest za długi');
			} else if (p !== client.postal) {
				dto.postal = p;
			}
		}

		if (description !== undefined) {
			const d = description.trim();
			if (d === '') {
				dto.description = null;
			} else if (d.length > 256) {
				return message.error('Opis jest za długi');
			} else if (d !== client.description) {
				dto.description = d;
			}
		}

		if (Object.keys(dto).length === 0) {
			return message.error('Nie wprowadzono żadnych zmian');
		}

		const success = await saveChanges(dto);
		if (!success) {
			return;
		}
	};

	const actions = client.removedDate
		? [
				<Button key={0} type="primary" size="large" icon={<UndoOutlined />} loading={loading} onClick={restoreClient}>
					Przywróć klienta
				</Button>,
		  ]
		: [
				<AsyncButton key={1} icon={<CheckOutlined />} onClick={_saveChanges} disabled={!hasAnyChanges()}>
					Zapisz zmiany
				</AsyncButton>,
				<Button
					key={2}
					type="primary"
					danger
					size="large"
					icon={<DeleteOutlined />}
					onClick={() => setDeleteModal(true)}
				>
					Usuń klienta
				</Button>,
				<Button
					key={3}
					type="primary"
					size="large"
					icon={<PlusCircleOutlined />}
					onClick={() =>
						locked
							? message.error('Nie można dodać zamówienia, klient jest zablokowany.')
							: navigate(`/panel/new-order?client=${client?.id}`)
					}
				>
					Dodaj zamówienie na klienta
				</Button>,
		  ];
	if (userLevel === UserLevel.superAdmin) {
		actions.push(
			<AsyncButton
				key={locked ? 4 : 5}
				danger={!locked}
				icon={locked ? <UnlockOutlined /> : <LockOutlined />}
				onClick={locked ? unlockClient : lockClient}
			>
				{locked ? 'Odblokuj' : 'Zablokuj'}
			</AsyncButton>
		);
	}

	const totalOrdersSum = orders.reduce((acc, order) => acc + order.grossPrice, 0);
	const totalOverdueSum = orders.reduce((sum, order) => {
		if (order.paymentState !== ClientOrderPaymentState.overdue) {
			return sum;
		}

		const leftToPay = order.grossPrice - order.alreadyPaid;
		return sum + Math.max(leftToPay, 0);
	}, 0);

	return (
		<Page title={`Klienci / ${client?.name}`} actions={actions}>
			<div className="client-details">
				<div className="flex">
					<PageSection title="Informacje" width={902}>
						<Space size={25} className="inputs">
							<div>
								<label>Nazwa</label>
								<Input
									type="text"
									size="large"
									value={name ?? client.name}
									onChange={(e) => setName(e.target.value)}
									disabled={!!client.removedDate}
								/>
							</div>
							<div>
								<label>Typ</label>
								<Select
									size="large"
									value={type ?? client.type}
									onChange={(value) => setType(value)}
									disabled={!!client.removedDate}
								>
									<Option value={ClientType.company}>Firma</Option>
									<Option value={ClientType.private}>Osoba prywatna</Option>
								</Select>
							</div>
						</Space>
						<Space size={25} className="inputs">
							<div>
								<label>Numer telefonu</label>
								<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
									<Input
										type="text"
										size="large"
										value={phone ?? client.phone}
										onChange={(e) => setPhone(e.target.value)}
										disabled={!!client.removedDate}
									/>
									<a
										href={getTelHref(phone ?? client.phone)}
										title="Zadzwoń"
										style={{ display: 'flex', alignItems: 'center', fontSize: 22, lineHeight: 1 }}
									>
										<PhoneOutlined />
									</a>
								</div>
							</div>
							<div>
								<label>Email</label>
								<Input
									type="text"
									size="large"
									value={email ?? client.email ?? ''}
									onChange={(e) => setEmail(e.target.value)}
									disabled={!!client.removedDate}
								/>
							</div>
						</Space>
						<Space size={25} className="inputs">
							<div>
								<label>Numer NIP</label>
								<Input
									type="text"
									size="large"
									value={nip ?? client.nip ?? ''}
									onChange={(e) => setNip(e.target.value)}
									disabled={!!client.removedDate}
								/>
							</div>
							<div>
								<label>Numer BDO</label>
								<Input
									type="text"
									size="large"
									value={bdo ?? client.bdo ?? ''}
									onChange={(e) => setBdo(e.target.value)}
									disabled={!!client.removedDate}
								/>
							</div>
						</Space>
						<Space size={25} className="inputs">
							<div>
								<label>Ulica</label>
								<Input
									type="text"
									size="large"
									value={street ?? client.street ?? ''}
									onChange={(e) => setStreet(e.target.value)}
									disabled={!!client.removedDate}
								/>
							</div>
						</Space>
						<Space size={25} className="inputs">
							<div>
								<label>Miasto</label>
								<Input
									type="text"
									size="large"
									value={city ?? client.city ?? ''}
									onChange={(e) => setCity(e.target.value)}
									disabled={!!client.removedDate}
								/>
							</div>
							<div>
								<label>Kod pocztowy</label>
								<Input
									type="text"
									size="large"
									value={postal ?? client.postal ?? ''}
									onChange={(e) => setPostal(e.target.value)}
									disabled={!!client.removedDate}
								/>
							</div>
						</Space>
						<label>Opis</label>
						<TextArea
							rows={3}
							value={description ?? client.description ?? ''}
							onChange={(e) => setDescription(e.target.value)}
							disabled={!!client.removedDate}
						/>
						<Space size={25} direction="vertical">
							<table>
								<tbody>
									<tr>
										<td>
											Utworzony: <span className="user-data">{Formatting.epochToDate(client.createdDate)}</span>{' '}
										</td>
									</tr>
									<tr>
										<td>
											Przez:
											<span className="user-data">
												<Link to={`/panel/drivers/${createdBy.id}`}>
													{createdBy.name} {createdBy.surname}
												</Link>
											</span>
										</td>
									</tr>
								</tbody>
							</table>
							{locked && lockedBy && (
								<table>
									<tbody>
										<tr>
											<td>
												Zablokowany: <span className="user-data">{Formatting.epochToDate(client.lockedDate)}</span>{' '}
											</td>
										</tr>
										<tr>
											<td>
												Przez:
												<span className="user-data">
													<Link to={`/panel/drivers/${lockedBy.id}`}>
														{lockedBy.name} {lockedBy.surname}
													</Link>
												</span>
											</td>
										</tr>
									</tbody>
								</table>
							)}
							{removedBy && (
								<table>
									<tbody>
										<tr>
											<td>
												Usunięty: <span className="user-data">{Formatting.epochToDate(client.removedDate)}</span>{' '}
											</td>
										</tr>
										<tr>
											<td>
												Przez:
												<span className="user-data">
													<Link to={`/panel/drivers/${removedBy.id}`}>
														{removedBy.name} {removedBy.surname}
													</Link>
												</span>
											</td>
										</tr>
									</tbody>
								</table>
							)}
						</Space>
						<Space size={25} className="balance">
							<div className="flex flex-dir">
								<label>
									Łączna kwota zamówień brutto <span className="red">(zaległe)</span>
								</label>
								<span>
									{Formatting.money(totalOrdersSum)} <span className="red">({Formatting.money(totalOverdueSum)})</span>
								</span>
							</div>
						</Space>
					</PageSection>
					{/* {advances?.length !== 0 && (
								<PageSection title="Zaliczki" width={878}>
									<Timeline>
										{advances?.map((order) => {
											const date = Formatting.epochToDate(
												order.delivery_done,
												moment(order.delivery_done).hours() === 0
											);

											return (
												<Timeline.Item key={order.id}>
													<span>{date}</span> - {order.address}. {Formatting.money(order.advance)}{' '}
													<Link to={`/panel/orders/${order.id}`}>Szczegóły</Link>
												</Timeline.Item>
											);
										})}
									</Timeline>
								</PageSection>
							)} */}
					<OrdersSection
						client={client}
						orders={orders}
						addresses={addresses}
						containers={containers}
						payOrder={payOrder}
					/>
				</div>
			</div>

			<WarningModal
				visible={deleteModal}
				onCancel={() => setDeleteModal(false)}
				onOk={() => deleteClient(() => setDeleteModal(false))}
				loading={loading}
			>
				<h1>Czy na pewno chcesz usunąć klienta?</h1>
				<h3>Będzie go można później przywrócić</h3>
			</WarningModal>
		</Page>
	);
};
